import {
  attachmentDeleteAttachment,
  attachmentGetAttachment,
  initAttachmentUpload,
  attachmentGetAttachments
} from '@api/Attachment';
import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  AttachmentControllerDeleteAttachmentParams,
  AttachmentControllerGetAttachmentParams,
  AttachmentControllerGetAttachmentsParams,
  InitAttachmentUploadDto
} from 'src/api/Api';

export const QUERY_ATTACHMENT_FILE = 'attachment_file';
export const QUERY_ATTACHMENT_FILES = 'attachment_files';

export function useInitAttachmentUpload({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: InitAttachmentUploadDto) => {
      return await initAttachmentUpload(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useGetAttachment({
  params,
  enabled = true
}: {
  params: AttachmentControllerGetAttachmentParams;
  enabled?: boolean;
}) {
  const fetch = useQuery({
    queryKey: [QUERY_ATTACHMENT_FILE, { params }],
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await attachmentGetAttachment(params, {
        signal
      });
      return data;
    }
  });

  return fetch;
}

export function useGetAttachments({
  params,
  enabled = true
}: {
  params: AttachmentControllerGetAttachmentsParams;
  enabled?: boolean;
}) {
  const fetch = useQuery({
    queryKey: [QUERY_ATTACHMENT_FILES, { params }],
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await attachmentGetAttachments(params, {
        signal
      });
      return data;
    }
  });

  return fetch;
}

export function useDeleteAttachment({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: AttachmentControllerDeleteAttachmentParams) => {
      return await attachmentDeleteAttachment(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}
